import { useState, useRef, useEffect } from "react";
import { LogIn, LogOut, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export const AuthButton = ({ compact = false }) => {
  const { user, loading, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (loading) {
    return (
      <div
        className="h-9 w-24 rounded-sm bg-[#EAE5DC] animate-pulse"
        data-testid="auth-button-loading"
      />
    );
  }

  if (!user) {
    return (
      <button
        type="button"
        onClick={login}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-sm border border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-[#FAF9F6] transition-colors duration-300 text-xs tracking-wide"
        data-testid="auth-sign-in-button"
      >
        <LogIn size={14} strokeWidth={1.8} />
        {compact ? "Sign in" : "Sign in with Google"}
      </button>
    );
  }

  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="relative" ref={ref} data-testid="auth-user-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2.5 group"
        data-testid="auth-user-trigger"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name || user.email}
            referrerPolicy="no-referrer"
            className="w-8 h-8 rounded-full object-cover border border-[#D8D3CC]"
          />
        ) : (
          <span className="w-8 h-8 rounded-full bg-[#4A6B53] text-[#FAF9F6] inline-flex items-center justify-center text-sm font-serif">
            {initial}
          </span>
        )}
        <span className="hidden md:inline text-xs tracking-wide text-[#2C2A28] group-hover:text-[#4A6B53] transition-colors">
          {(user.name || user.email).split(" ")[0]}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-3 w-64 rounded-sm border border-[#D8D3CC] bg-[#FAF9F6] shadow-lg z-50"
          data-testid="auth-user-dropdown"
        >
          <div className="px-4 py-3 border-b border-[#D8D3CC]">
            <p className="text-[10px] tracking-[0.18em] uppercase text-[#5C5852]">
              Signed in
            </p>
            <p className="font-serif text-base text-[#2C2A28] mt-1 truncate">
              {user.name || user.email}
            </p>
            <p className="text-xs text-[#5C5852] truncate">{user.email}</p>
            {user.isAdmin && (
              <span className="inline-block mt-2 text-[10px] tracking-[0.18em] uppercase text-[#CC7A52] font-bold">
                · Admin
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="w-full text-left px-4 py-3 text-sm text-[#2C2A28] hover:bg-[#EAE5DC] inline-flex items-center gap-2"
            data-testid="auth-sign-out-button"
          >
            <LogOut size={14} strokeWidth={1.8} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthButton;
